<template>
  <div>
    <PageAllow controller="Suppliers" action="index" />
    <Breadcrumbs :items="items" />
    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherKeyword
                ref="keyword"
                v-model="supplierKeyword"
                @text="supplierKeywordText($event)"
                :label="$t('Supplier.Keyword')"
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                small
                color="success"
                dark
                @click.stop="submit()"
                class="ms-1"
              >
                <v-icon x-small class="pe-1">fas fa-save</v-icon>
                {{ $t("AnnouncementShop.submit") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>

        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <v-col cols="12" sm="6" md="4">
                  <div
                    class="caption grey--text input-chip-group-label"
                    :class="
                      $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                    "
                  >
                    {{ $t("AnnouncementShop.orderToPurchase.title") }}
                  </div>
                  <v-radio-group row v-model="orderToPurchase">
                    <v-radio
                      :label="$t('AnnouncementShop.orderToPurchase.all')"
                      key="all"
                      value="all"
                    />
                    <v-radio
                      :label="$t('AnnouncementShop.orderToPurchase.off')"
                      :key="0"
                      :value="0"
                    />
                    <v-radio
                      :label="$t('AnnouncementShop.orderToPurchase.on')"
                      :key="1"
                      :value="1"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="12" sm="6" md="8">
                  <SearcherStatus
                    v-model="suppliersStatus"
                    :label="$t('AnnouncementShop.status.title')"
                    :items="$consts.stop_flg"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="suppliersItems"
            :loading="loading"
            multi-sort
            class="elevation-2 px-4 pt-1"
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.choose>
              <v-checkbox
                class="mt-2 mb-n3"
                color="primary"
                :indeterminate="
                  suppliersItems.find((detail) => detail.choose != chooseAll)
                    ? true
                    : false
                "
                v-model="chooseAll"
                :true-value="1"
                :false-value="0"
              >
                <template v-slot:label>
                  <span class="caption ms-n2">
                    {{ $t("CRUD.chooseAll") }}
                  </span>
                </template>
              </v-checkbox>
            </template>

            <template v-slot:item.choose="{ item }">
              <div class="d-flex mt-n2 mb-n3">
                <v-checkbox
                  color="primary"
                  v-model="item.choose"
                  :true-value="1"
                  :false-value="0"
                  :disabled="item.disabled"
                >
                  <template v-slot:label>
                    <span class="caption ms-n2">
                      {{ $t(item.choose ? "CRUD.choosed" : "CRUD.unchoose") }}
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Announcement from "@/api/announcement";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import Suppliers from "@/api/suppliers";
import SearcherStatus from "@/components/plugins/SearcherStatus";

const defaultOptions = {
  keyword: "",
  type: null,
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherStatus,
  },

  data: () => ({
    options: JSON.parse(JSON.stringify(defaultOptions)),
    suppliers: [],
    chooseAll: 0,
    supplierKeyword: "",
    orderToPurchase: "all",
    suppliersStatus: [0],
    push_supplier_ids: [],
  }),

  computed: {
    suppliersItems() {
      return this.abc();
    },
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.customerMaintenance"),
        disabled: false,
        href: "/qacenter",
      },
      {
        text: i18n.t("Nav.announcement"),
        disabled: false,
        href: "/announcement",
      },
      {
        text: i18n.t("Nav.announcementSuppliers"),
        disabled: true,
      },
    ],
    headers() {
      return [
        {
          text: i18n.t("Supplier.Fields.supplier_code"),
          align: "start",
          sortable: false,
          value: "supplier_code",
        },
        {
          text: i18n.t("Supplier.Fields.supplier_name"),
          align: "start",
          sortable: false,
          value: "supplier_name",
        },
        {
          text: i18n.t("Supplier.Fields.supply_type"),
          align: "start",
          sortable: false,
          class: "text-no-wrap",
          value: "supply_type",
        },
        {
          text: i18n.t("CRUD.chooseAll"),
          align: "start",
          sortable: false,
          filterable: true,
          value: "choose",
        },
      ];
    },
    ...mapState(["loading"]),
  },
  mounted() {
    Announcement.fetchOne(this.$route.params.id).then(
      function (response) {
        if (response.data.status.isOK()) {
          this.push_supplier_ids = response.data.announcement.push_supplier_ids;
        }
      }.bind(this)
    );
    this.getSuppliersFromAPI();
  },
  methods: {
    resetSearch() {
      this.orderToPurchase = "all";
      [this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.abc();
    },

    abc() {
      return this.suppliers.filter(
        (e) =>
          ((this.suppliersStatus.length &&
            this.suppliersStatus.find((flg) => flg == e.stop_flg) !=
              undefined) ||
            !this.suppliersStatus.length) &&
          ((this.supplierKeyword &&
            e.supplier_code.indexOf(this.supplierKeyword) > -1) ||
            e.supplier_name.indexOf(this.supplierKeyword) > -1 ||
            !this.supplierKeyword) &&
          ((this.orderToPurchase != "all" &&
            this.orderToPurchase == e.order_to_purchase) ||
            this.orderToPurchase == "all")
      );
    },
    supplierKeywordText(e) {
      this.supplierKeyword = e;
    },

    getSuppliersFromAPI() {
      Suppliers.fetchAll(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            var suppliers = response.data.suppliers;
            suppliers.forEach((detail) => {
              detail.choose = 0;
            });
            this.suppliers = suppliers;
            this.suppliers.map((v) => {
              if (this.push_supplier_ids.includes(v.id)) {
                v.choose = 1;
              }
              return v;
            });
          }
        }.bind(this)
      );
    },

    submit() {
      let supplier_ids = this.suppliers
        .filter((v) => !!v.choose)
        .map((v) => v.id);

      var data = [];
      data.push({
        id: this.$route.params.id,
        supplier_ids: supplier_ids,
      });
      Announcement.pushShop(data[0]).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$router.push("/announcement");
          }
        }.bind(this)
      );
    },
  },

  watch: {
    push_supplier_ids: {
      deep: true,
      handler() {
        this.suppliers.map((v) => {
          if (this.push_supplier_ids.includes(v.id)) {
            v.choose = 1;
          }
          return v;
        });
      },
    },

    chooseAll: {
      handler() {
        this.suppliersItems.forEach(
          function (e) {
            e.choose = this.chooseAll ? 1 : 0;
          }.bind(this)
        );
      },
    },
  },
};
</script>

<style>
</style>